import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import { Button } from "../forms/Buttons/Button";
import { deleteQuestionFolder } from "../../services/questionService";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";

// Confirms deleting an exam or one of its subjects (`folder`; `examName` names a subject's exam). A 409
// folder_not_empty (with questions_count) is shown in the dialog; so is a 409 folder_in_use (BTS variants or
// booklets are built from it), after which the delete is not offered again.
export default function DeleteFolderDialog({ isOpen, folder, examName = "", onClose, onDeleted }) {
  const [t] = useTranslation();
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [inUse, setInUse] = useState(false);
  const isSubject = !!folder?.parent;

  useEffect(() => {
    if (isOpen) {
      setError("");
      setInUse(false);
    }
  }, [isOpen]);

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await deleteQuestionFolder(folder.uuid);
      onDeleted(folder);
    } catch (err) {
      if (getApiErrorCode(err) === "folder_in_use") {
        setInUse(true);
        setError(isSubject ? t("folders.delete_dialog.in_use_subject") : t("folders.delete_dialog.in_use_exam"));
      } else {
        setError(getApiErrorMessage(t, err, "folders.toast.delete_failed"));
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <CustomDialog
      isOpen={isOpen}
      closeModal={() => !deleting && onClose()}
      headerText={isSubject ? t("folders.delete_dialog.subject_title") : t("folders.delete_dialog.exam_title")}
      maxWidth="max-w-md"
    >
      <p className="mt-2 text-sm text-slate-600">
        {isSubject
          ? t("folders.delete_dialog.subject_text", { name: folder?.name || "", exam: examName })
          : t("folders.delete_dialog.exam_text", { name: folder?.name || "" })}
      </p>
      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={onClose} disabled={deleting} className="max-sm:h-11" />
        <Button
          type="button"
          color="error"
          text={t("buttons.delete")}
          onClick={handleDelete}
          loading={deleting}
          disabled={deleting || inUse}
          className="max-sm:h-11"
        />
      </div>
    </CustomDialog>
  );
}
